import React, { useCallback, useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { advanceLegacyReport, createReportAppointmentOrders, createReportRevision, getReviewedReports } from '../api/reportApi';
import Pagination from '../components/Pagination';
import {
  APPOINTMENT_ORDER_OPTION_GROUPS,
  getAppointmentOrderGroupLabel
} from '../utils/appointmentOrderOptions';
import { formatDateTime, formatPatientName, normalizeUiLocale } from '../utils/locale';

const ReviewedReportsList = () => {
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [filterColumn, setFilterColumn] = useState('id');
  const [filterValue, setFilterValue] = useState('');
  const [filteredReports, setFilteredReports] = useState([]);
  const [allReports, setAllReports] = useState([]);
  const [selectedReportForOrders, setSelectedReportForOrders] = useState(null);
  const [appointmentDrafts, setAppointmentDrafts] = useState([{ check_type: '', description: '' }]);
  const [creatingAppointmentOrders, setCreatingAppointmentOrders] = useState(false);
  const [appointmentOrdersModalError, setAppointmentOrdersModalError] = useState('');
  const [workflowActionReportId, setWorkflowActionReportId] = useState(null);

  const { t, i18n } = useTranslation();
  const locale = normalizeUiLocale(i18n.resolvedLanguage || i18n.language);
  const location = useLocation();
  const navigate = useNavigate();

  const filterOptions = [
    { value: 'id', label: t('reports.reportId') },
    { value: 'visit_id', label: t('reports.visitId') },
    { value: 'patient_name', label: t('reports.patientName') },
    { value: 'clinic_name', label: t('reports.clinicName') },
    { value: 'report_type', label: t('reports.reportType') },
    { value: 'status', label: t('reports.status') },
    { value: 'reviewer_name', label: t('reports.reviewerName') },
    { value: 'workflow', label: t('reports.workflow') },
    { value: 'stage', label: t('reports.stage') },
    { value: 'visit_type', label: t('reports.visitType') }
  ];

  const filterReports = useCallback((data, column, value) => {
    if (!value || value.trim() === '') {
      return data;
    }

    return data.filter((report) => {
      const fieldValue = report[column];
      if (fieldValue === null || fieldValue === undefined) {
        return false;
      }

      return String(fieldValue).toLowerCase().includes(value.toLowerCase().trim());
    });
  }, []);

  const paginateData = useCallback((data, page, size) => {
    const safeTotalPages = Math.max(1, Math.ceil(data.length / size));
    const safePage = Math.min(page, safeTotalPages);
    const startIndex = (safePage - 1) * size;
    const endIndex = startIndex + size;

    return {
      currentPage: safePage,
      totalPages: safeTotalPages,
      totalCount: data.length,
      rows: data.slice(startIndex, endIndex)
    };
  }, []);

  const getPresetFilterFromSearch = useCallback((search) => {
    const params = new URLSearchParams(search);
    const visitID = params.get('visit_id');
    if (visitID) {
      return {
        column: 'visit_id',
        value: visitID
      };
    }

    return {
      column: 'id',
      value: ''
    };
  }, []);

  const buildReviewedVisitLink = (visitId) => `/reviewed-reports?visit_id=${visitId}`;

  const fetchReports = useCallback(async (page, size, nextFilterColumn, nextFilterValue) => {
    try {
      setLoading(true);
      const allData = await getReviewedReports(1, 1000);
      const data = allData || [];
      const filtered = filterReports(data, nextFilterColumn, nextFilterValue);
      const pageData = paginateData(filtered, page, size);

      setAllReports(data);
      setFilteredReports(filtered);
      setReports(pageData.rows);
      setCurrentPage(pageData.currentPage);
      setPageSize(size);
      setTotalCount(pageData.totalCount);
      setTotalPages(pageData.totalPages);
      setFilterColumn(nextFilterColumn);
      setFilterValue(nextFilterValue);
      setError('');
      setSuccessMessage('');
    } catch (requestError) {
      console.error('获取已审核报告列表失败:', requestError);
      setError(`${t('reports.fetchError')}: ${requestError.response?.data?.error || t('common.unknownError')}`);
    } finally {
      setLoading(false);
    }
  }, [filterReports, paginateData, t]);

  const getVisitTypeDisplay = (visitType) => {
    if (visitType === 'follow_up') {
      return t('reports.visitTypeFollowUp');
    }
    if (visitType === 'initial') {
      return t('reports.visitTypeInitial');
    }
    if (visitType === 'checkup') {
      return t('reports.visitTypeCheckup');
    }
    return t('reports.visitTypeUnknown');
  };

  const getRelatedVisitsDisplay = (relatedVisitIds) => {
    if (!relatedVisitIds || relatedVisitIds.length === 0) {
      return t('reports.noLinkedSourceVisits');
    }

    return t('reports.relatedVisitsValue', {
      visits: relatedVisitIds.map((visitId) => `#${visitId}`).join(' / ')
    });
  };

  useEffect(() => {
    const preset = getPresetFilterFromSearch(location.search);
    fetchReports(1, 10, preset.column, preset.value);
  }, [location.search, getPresetFilterFromSearch, fetchReports]);

  const handleSearch = () => {
    const filtered = filterReports(allReports, filterColumn, filterValue);
    const pageData = paginateData(filtered, 1, pageSize);

    setFilteredReports(filtered);
    setReports(pageData.rows);
    setCurrentPage(pageData.currentPage);
    setTotalCount(pageData.totalCount);
    setTotalPages(pageData.totalPages);
  };

  const handleReset = () => {
    fetchReports(1, pageSize, filterColumn, '');
  };

  const handlePageChange = (page) => {
    const pageData = paginateData(filteredReports, page, pageSize);
    setReports(pageData.rows);
    setCurrentPage(pageData.currentPage);
  };

  const handlePageSizeChange = (nextPageSize) => {
    const pageData = paginateData(filteredReports, 1, nextPageSize);
    setReports(pageData.rows);
    setPageSize(nextPageSize);
    setCurrentPage(pageData.currentPage);
    setTotalPages(pageData.totalPages);
  };

  const formatReportDate = (dateString) => formatDateTime(dateString, locale);
  const getReportPatientName = (report) => formatPatientName(
    report,
    locale,
    report?.patient_name || t('common.unknown')
  );

  const getReportTypeDisplay = (reportType) => {
    if (!reportType) {
      return t('reports.unknownType');
    }

    const type = reportType.toLowerCase();

    if (type === 'proposal' || type === '建议' || type === '初步') {
      return t('reports.proposalReport');
    }

    if (type === 'final' || type === '最终' || type === '终结') {
      return t('reports.finalReport');
    }

    return reportType.charAt(0).toUpperCase() + reportType.slice(1);
  };

  const getStatusClassName = (status) => {
    if (status === 'approved') {
      return 'status-pill status-pill--success';
    }

    if (status === 'rejected') {
      return 'status-pill status-pill--danger';
    }

    return 'status-pill status-pill--neutral';
  };

  const getStatusText = (status) => {
    if (status === 'approved') {
      return t('reports.approved');
    }

    if (status === 'rejected') {
      return t('reports.rejected');
    }

    return status || t('common.unknown');
  };

  const canManageAppointmentOrders = (report) => (
    report?.report_type === 'final' && report?.status === 'approved' && report?.is_effective_report !== false
  );
  const isAppointmentOrdersDialogOpen = Boolean(selectedReportForOrders);

  const handleWorkflowAction = async (report, action) => {
    try {
      setWorkflowActionReportId(report.id);
      setError('');
      const result = action === 'create_revision'
        ? await createReportRevision(report.id)
        : await advanceLegacyReport(report.id);
      const nextReportId = result?.report?.id;
      if (!nextReportId) {
        throw new Error('The backend did not return the next report');
      }
      navigate(`/reports/${nextReportId}/review`);
    } catch (requestError) {
      setError(`${t('reports.reviewError')}: ${requestError.response?.data?.error || requestError.message || t('common.unknownError')}`);
    } finally {
      setWorkflowActionReportId(null);
    }
  };

  const openAppointmentOrdersDialog = (report) => {
    setSelectedReportForOrders(report);
    setAppointmentDrafts([{ check_type: '', description: '' }]);
    setAppointmentOrdersModalError('');
    setError('');
  };

  const closeAppointmentOrdersDialog = (force = false) => {
    if (creatingAppointmentOrders && !force) {
      return;
    }
    setSelectedReportForOrders(null);
    setAppointmentDrafts([{ check_type: '', description: '' }]);
    setAppointmentOrdersModalError('');
  };

  const addAppointmentDraft = () => {
    setAppointmentDrafts((current) => [...current, { check_type: '', description: '' }]);
  };

  const updateAppointmentDraft = (index, field, value) => {
    setAppointmentDrafts((current) =>
      current.map((draft, draftIndex) => (
        draftIndex === index ? { ...draft, [field]: value } : draft
      ))
    );
  };

  const removeAppointmentDraft = (index) => {
    setAppointmentDrafts((current) => {
      if (current.length === 1) {
        return [{ check_type: '', description: '' }];
      }
      return current.filter((_, draftIndex) => draftIndex !== index);
    });
  };

  const handleCreateAppointmentOrders = async () => {
    if (!selectedReportForOrders?.id) {
      setAppointmentOrdersModalError(t('reports.fetchReportError'));
      return;
    }

    const appointmentOrdersPayload = appointmentDrafts
      .map((draft) => ({
        check_type: draft.check_type.trim(),
        description: draft.description.trim()
      }))
      .filter((draft) => draft.check_type);

    if (appointmentOrdersPayload.length === 0) {
      setAppointmentOrdersModalError(t('reports.appointmentOrdersValidationError'));
      return;
    }

    try {
      setCreatingAppointmentOrders(true);
      setAppointmentOrdersModalError('');
      setError('');
      setSuccessMessage('');

      await createReportAppointmentOrders(selectedReportForOrders.id, appointmentOrdersPayload);

      setSuccessMessage(t('reports.appointmentOrdersCreateSuccess'));
      closeAppointmentOrdersDialog(true);
      window.setTimeout(() => setSuccessMessage(''), 3000);
    } catch (requestError) {
      console.error(`为报告ID${selectedReportForOrders.id}补开预约单失败:`, requestError.response?.data || requestError.message || requestError);
      setAppointmentOrdersModalError(
        t('reports.appointmentOrdersCreateError')
      );
    } finally {
      setCreatingAppointmentOrders(false);
    }
  };

  return (
    <div className="stack">
      <div className="page-header">
        <div className="page-title-block">
          <h1 className="page-title">{t('reports.reviewedReportsList')}</h1>
        </div>

        <div className="page-actions">
          <button type="button" className="theme-button theme-button--secondary" onClick={() => fetchReports(1, pageSize, filterColumn, filterValue)}>
            {t('common.refresh')}
          </button>
        </div>
      </div>

      {error && (
        <div className="status-banner status-banner--error">
          <span>{error}</span>
        </div>
      )}

      {successMessage && (
        <div className="status-banner status-banner--success">
          <span>{successMessage}</span>
        </div>
      )}

      <section className="surface-panel">
        <div className="surface-panel__body stack">
          <div className="toolbar-cluster">
            <span className="status-pill status-pill--success">
              {t('reports.reviewedCount', { count: totalCount })}
            </span>
            <span className="status-pill status-pill--neutral">
              {t('reports.pageCount', { current: currentPage, total: totalPages })}
            </span>
          </div>

          <div className="filter-row">
            <div className="field">
              <label className="field__label" htmlFor="reviewed-filter-column">
                {t('common.filterBy')}
              </label>
              <select
                id="reviewed-filter-column"
                className="theme-select"
                value={filterColumn}
                onChange={(event) => setFilterColumn(event.target.value)}
              >
                {filterOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>

            <div className="field">
              <label className="field__label" htmlFor="reviewed-filter-value">
                {t('common.search')}
              </label>
              <input
                id="reviewed-filter-value"
                className="theme-input"
                type="text"
                value={filterValue}
                onChange={(event) => setFilterValue(event.target.value)}
                onKeyDown={(event) => {
                  if (event.key === 'Enter') {
                    handleSearch();
                  }
                }}
                placeholder={t('common.enterSearchValue')}
              />
            </div>

            <button type="button" className="theme-button theme-button--primary" onClick={handleSearch}>
              {t('common.search')}
            </button>
            <button type="button" className="theme-button theme-button--ghost" onClick={handleReset}>
              {t('common.reset')}
            </button>
          </div>

          {loading ? (
            <div className="loading-state">
              <div className="loading-orb" />
              <span>{t('common.loading')}</span>
            </div>
          ) : reports.length === 0 ? (
            <div className="empty-state">
              <h3 className="auth-form__title">{t('reports.noReviewedReports')}</h3>
            </div>
          ) : (
            <div className="data-table-shell">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>{t('reports.reportId')}</th>
                    <th>{t('reports.patientName')}</th>
                    <th>{t('reports.clinicName')}</th>
                    <th>{t('reports.reportType')}</th>
                    <th>{t('reports.status')}</th>
                    <th>{t('reports.reviewer')}</th>
                    <th>{t('reports.reviewedAt')}</th>
                    <th>{t('reports.actions')}</th>
                  </tr>
                </thead>
                <tbody>
                  {reports.map((report) => (
                    <tr key={report.id}>
                      <td>
                        <div className="table-title">#{report.id}</div>
                        <div className="table-meta">{t('reports.visitId')}: {report.visit_id}</div>
                        <div className="table-meta">{getRelatedVisitsDisplay(report.related_visit_ids)}</div>
                        {report.related_visit_ids?.length > 0 && (
                          <div className="visit-chip-list">
                            {report.related_visit_ids.map((visitId) => (
                              <Link key={`${report.id}-${visitId}`} to={buildReviewedVisitLink(visitId)} className="visit-chip">
                                #{visitId}
                              </Link>
                            ))}
                          </div>
                        )}
                      </td>
                      <td>
                        {report.patient_id ? (
                          <Link className="table-link-button table-link-button--inline" to={`/patients/${report.patient_id}`}>
                            <bdi dir="auto">{getReportPatientName(report)}</bdi>
                          </Link>
                        ) : (
                          <div className="table-title"><bdi dir="auto">{getReportPatientName(report)}</bdi></div>
                        )}
                      </td>
                      <td>
                        <div className="table-title">{report.clinic_name || t('reports.notSpecified')}</div>
                      </td>
                      <td>
                        <span className="status-pill status-pill--warning">
                          {getReportTypeDisplay(report.report_type)}
                        </span>
                        <div style={{ marginTop: '0.5rem' }}>
                          <span className={`status-pill ${report.visit_type === 'follow_up' ? 'status-pill--success' : 'status-pill--neutral'}`}>
                            {getVisitTypeDisplay(report.visit_type)}
                          </span>
                        </div>
                        {report.legacy_link_ambiguous && (
                          <div style={{ marginTop: '0.5rem' }}>
                            <span className="status-pill status-pill--danger">{t('reports.ambiguousLegacyLink')}</span>
                          </div>
                        )}
                        {(report.predecessor_report_id || report.successor_report_id) && (
                          <div className="visit-chip-list" style={{ marginTop: '0.5rem' }}>
                            {report.predecessor_report_id && (
                              <Link to={`/reports/${report.predecessor_report_id}/view`} className="visit-chip">
                                {t('reports.previousStage')} #{report.predecessor_report_id}
                              </Link>
                            )}
                            {report.successor_report_id && (
                              <Link to={`/reports/${report.successor_report_id}/review`} className="visit-chip">
                                {t('reports.nextStage')} #{report.successor_report_id}
                              </Link>
                            )}
                          </div>
                        )}
                        <div className="table-meta" style={{ marginTop: '0.5rem' }}>
                          {(report.workflow === 'single_review' || report.report_flow_version === 2)
                            ? t('reports.singleReviewFlow')
                            : t('reports.legacyFlow')}
                          {report.revision_number ? ` · ${t('reports.revision')} ${report.revision_number}` : ''}
                        </div>
                        <div style={{ marginTop: '0.5rem' }}>
                          {report.is_effective_report ? (
                            <span className="status-pill status-pill--success">{t('reports.effectiveReport')}</span>
                          ) : report.successor_report_id ? (
                            <span className="status-pill status-pill--neutral">{t('reports.supersededReport')}</span>
                          ) : (
                            <span className="status-pill status-pill--neutral">{t('reports.historicalStage')}</span>
                          )}
                        </div>
                      </td>
                      <td>
                        <span className={getStatusClassName(report.status)}>
                          {getStatusText(report.status)}
                        </span>
                      </td>
                      <td>{report.reviewer_name || t('reports.notReviewed')}</td>
                      <td>{report.reviewed_at ? formatReportDate(report.reviewed_at) : t('reports.notReviewed')}</td>
                      <td>
                        <div className="table-actions">
                          <Link
                            to={`/reports/${report.id}/view`}
                            className="theme-button theme-button--ghost"
                          >
                            {t('reports.viewReport')}
                          </Link>
                          {canManageAppointmentOrders(report) && (
                            <button
                              type="button"
                              className="theme-button theme-button--secondary"
                              onClick={() => openAppointmentOrdersDialog(report)}
                            >
                              {t('reports.manageAppointmentOrders')}
                            </button>
                          )}
                          {report.available_actions?.includes('create_revision') && (
                            <button
                              type="button"
                              className="theme-button theme-button--secondary"
                              disabled={workflowActionReportId === report.id}
                              onClick={() => handleWorkflowAction(report, 'create_revision')}
                            >
                              {t('reports.createRevision')}
                            </button>
                          )}
                          {report.available_actions?.includes('advance_legacy') && (
                            <button
                              type="button"
                              className="theme-button theme-button--secondary"
                              disabled={workflowActionReportId === report.id}
                              onClick={() => handleWorkflowAction(report, 'advance_legacy')}
                            >
                              {t('reports.advanceLegacy')}
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {!loading && reports.length > 0 && (
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              totalCount={totalCount}
              pageSize={pageSize}
              onPageChange={handlePageChange}
              onPageSizeChange={handlePageSizeChange}
            />
          )}
        </div>
      </section>

      {isAppointmentOrdersDialogOpen && (
        <div className="dialog-backdrop">
          <div
            className="dialog-shell"
            style={{ width: 'min(100%, 48rem)', maxHeight: 'min(90vh, 52rem)', overflowY: 'auto' }}
            onClick={(event) => event.stopPropagation()}
          >
            <div style={{ display: 'grid', gap: '0.45rem' }}>
              <h2 className="dialog-shell__title">{t('reports.manageAppointmentOrders')}</h2>
              <p className="dialog-shell__message">{t('reports.appointmentOrdersCreateDescription')}</p>
            </div>

            <div style={{
              marginTop: '1rem',
              padding: '0.9rem 1rem',
              borderRadius: '1rem',
              border: '1px solid rgba(255, 255, 255, 0.08)',
              background: 'rgba(255, 255, 255, 0.04)',
              display: 'grid',
              gap: '0.35rem'
            }}>
              <div><strong>{t('reports.reportId')}:</strong> #{selectedReportForOrders.id}</div>
              <div><strong>{t('reports.visitId')}:</strong> #{selectedReportForOrders.visit_id}</div>
              <div><strong>{t('reports.patientName')}:</strong> <bdi dir="auto">{getReportPatientName(selectedReportForOrders)}</bdi></div>
            </div>

            {appointmentOrdersModalError && (
              <div className="status-banner status-banner--error" style={{ marginTop: '1rem' }}>
                <span>{appointmentOrdersModalError}</span>
              </div>
            )}

            <div style={{ display: 'grid', gap: '0.9rem', marginTop: '1.2rem' }}>
              {appointmentDrafts.map((draft, index) => (
                <div
                  key={`reviewed-appointment-draft-${index}`}
                  style={{
                    display: 'grid',
                    gridTemplateColumns: 'minmax(10rem, 13rem) minmax(0, 1fr) auto',
                    gap: '0.75rem',
                    alignItems: 'start'
                  }}
                >
                  <select
                    className="theme-select"
                    value={draft.check_type}
                    onChange={(event) => updateAppointmentDraft(index, 'check_type', event.target.value)}
                    disabled={creatingAppointmentOrders}
                  >
                    <option value="">{t('reports.selectCheckType')}</option>
                    {APPOINTMENT_ORDER_OPTION_GROUPS.map((group) => (
                      <optgroup key={group.id} label={getAppointmentOrderGroupLabel(group, t)}>
                        {group.options.map((option) => (
                          <option key={option.value} value={option.value}>
                            {t(option.labelKey)}
                          </option>
                        ))}
                      </optgroup>
                    ))}
                  </select>

                  <input
                    className="theme-input"
                    type="text"
                    value={draft.description}
                    onChange={(event) => updateAppointmentDraft(index, 'description', event.target.value)}
                    placeholder={t('reports.appointmentOrdersDescriptionPlaceholder')}
                    disabled={creatingAppointmentOrders}
                  />

                  <button
                    type="button"
                    className="theme-button theme-button--ghost"
                    onClick={() => removeAppointmentDraft(index)}
                    disabled={creatingAppointmentOrders}
                  >
                    {t('reports.remove')}
                  </button>
                </div>
              ))}
            </div>

            <div style={{ display: 'flex', justifyContent: 'space-between', gap: '0.8rem', flexWrap: 'wrap', marginTop: '1.2rem' }}>
              <button
                type="button"
                className="theme-button theme-button--secondary"
                onClick={addAppointmentDraft}
                disabled={creatingAppointmentOrders}
              >
                {t('reports.addAppointmentOrder')}
              </button>
              <div className="dialog-shell__actions" style={{ marginTop: 0 }}>
                <button
                  type="button"
                  className="theme-button theme-button--ghost"
                  onClick={() => closeAppointmentOrdersDialog()}
                  disabled={creatingAppointmentOrders}
                >
                  {t('common.cancel')}
                </button>
                <button
                  type="button"
                  className="theme-button theme-button--primary"
                  onClick={handleCreateAppointmentOrders}
                  disabled={creatingAppointmentOrders}
                >
                  {creatingAppointmentOrders ? t('reports.appointmentOrdersCreating') : t('reports.appointmentOrdersSubmit')}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ReviewedReportsList;
